import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ComposeEmailModal } from './ComposeEmailModal';

// ============================================================
// Awaiting Reply
// ============================================================
// The "don't let leads slip" tab. Lists email threads where the most
// recent message is inbound (someone reached out, or replied to you)
// and you haven't sent anything back. Sorted oldest-first so the
// people you've gone longest without replying to surface.

interface AwaitingReplyRow {
  thread_id: string;
  last_email_id: number;
  last_subject: string | null;
  last_preview: string | null;
  last_from_email: string | null;
  last_from_name: string | null;
  last_sent_at: string;
  last_ai_importance_score: number | null;
  last_ai_category: string | null;
  last_ai_summary: string | null;
  last_ai_action: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_company_id: number | null;
  company_id: number | null;
  company_name: string | null;
  linked_contact_id: number | null;
  linked_company_id: number | null;
}

interface CategoryCount {
  category: string;
  total: number;
  high_signal: number;
}

// Human-readable label + colour for each AI category.
const CATEGORY_META: Record<string, { label: string; cls: string }> = {
  lead_inquiry:    { label: 'Lead inquiry',    cls: 'bg-green-100 text-green-800' },
  customer_reply:  { label: 'Customer reply',  cls: 'bg-blue-100 text-blue-800' },
  scheduling:      { label: 'Scheduling',      cls: 'bg-cyan-100 text-cyan-800' },
  support:         { label: 'Support',         cls: 'bg-purple-100 text-purple-800' },
  cold_outreach:   { label: 'Cold pitch',      cls: 'bg-amber-100 text-amber-800' },
  newsletter:      { label: 'Newsletter',      cls: 'bg-gray-100 text-gray-600' },
  transactional:   { label: 'Transactional',   cls: 'bg-gray-100 text-gray-600' },
  internal:        { label: 'Internal',        cls: 'bg-indigo-100 text-indigo-800' },
  personal:        { label: 'Personal',        cls: 'bg-pink-100 text-pink-800' },
  unknown:         { label: 'Unknown',         cls: 'bg-gray-100 text-gray-500' },
  unscored:        { label: 'Unscored',        cls: 'bg-gray-50 text-gray-400' }
};

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const daysAgo = (iso: string): number => {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};
const formatRelative = (iso: string): string => {
  const days = daysAgo(iso);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};
const toneFor = (days: number): string => {
  if (days >= 14) return 'bg-red-50 border-red-200 text-red-900';
  if (days >= 7)  return 'bg-amber-50 border-amber-200 text-amber-900';
  return 'bg-blue-50 border-blue-200 text-blue-900';
};

export const AwaitingReplyView: React.FC = () => {
  const [rows, setRows] = useState<AwaitingReplyRow[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<CategoryCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [olderThan, setOlderThan] = useState(2);
  const [newerThan, setNewerThan] = useState(60);
  const [minImportance, setMinImportance] = useState(60); // default: high signal only
  const [category, setCategory] = useState<string>('all');
  const [syncing, setSyncing] = useState(false);
  const [triaging, setTriaging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<AwaitingReplyRow | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        olderThanDays: String(olderThan),
        newerThanDays: String(newerThan),
        minImportance: String(minImportance),
        category
      });
      const res = await fetch(`/api/crm/awaiting-reply?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load awaiting-reply');
      const data = await res.json();
      setRows(data.awaitingReply || []);
      setCategoryCounts(data.categoryCounts || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const triageNow = async () => {
    setTriaging(true);
    setError(null);
    try {
      const res = await fetch('/api/crm/emails/triage', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ batchSize: 100 })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Triage failed');
      setError(`Triaged ${data.scored} emails (${data.errors} errors). Refreshing…`);
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setTriaging(false);
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = await fetch('/api/crm/outlook/sync-emails', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ lookbackDays: 90 })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'MAIL_READ_MISSING') {
          setError(
            'Mail.Read permission not granted on your Azure app yet. ' +
            'Go to portal.azure.com → Marketmapr app → API permissions → Add → Microsoft Graph → Delegated → tick "Mail.Read" → Grant admin consent. ' +
            'Then disconnect + reconnect Outlook from the CRM page.'
          );
          return;
        }
        throw new Error(data.error || 'Sync failed');
      }
      setError(`Synced — ${data.created} new emails (${data.fetched} fetched, ${data.linkedToContact} matched to contacts).`);
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [olderThan, newerThan, minImportance, category]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-900">🔥 Awaiting your reply</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Email threads where the last message is from someone else and you haven't sent anything back.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500">Stale</span>
          <select value={olderThan} onChange={e => setOlderThan(parseInt(e.target.value, 10))}
            className="px-2 py-1 border border-gray-300 rounded">
            <option value={0}>any age</option>
            <option value={2}>2+ days</option>
            <option value={5}>5+ days</option>
            <option value={7}>1+ week</option>
            <option value={14}>2+ weeks</option>
          </select>
          <span className="text-gray-500">within</span>
          <select value={newerThan} onChange={e => setNewerThan(parseInt(e.target.value, 10))}
            className="px-2 py-1 border border-gray-300 rounded">
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={180}>6 months</option>
            <option value={365}>1 year</option>
          </select>
          <span className="text-gray-500 ml-2">AI score ≥</span>
          <select value={minImportance} onChange={e => setMinImportance(parseInt(e.target.value, 10))}
            className="px-2 py-1 border border-gray-300 rounded">
            <option value={0}>show all</option>
            <option value={40}>40 (cool)</option>
            <option value={60}>60 (signal)</option>
            <option value={80}>80 (hot)</option>
          </select>
        </div>
        <button
          onClick={triageNow}
          disabled={triaging}
          title="Run AI categorisation on any inbound emails that haven't been triaged yet"
          className="px-3 py-2 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          {triaging ? 'Triaging…' : '🧠 Triage now'}
        </button>
        <button
          onClick={triggerSync}
          disabled={syncing}
          className="px-3 py-2 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          {syncing ? 'Syncing…' : '📧 Sync emails'}
        </button>
      </div>

      {/* Category filter chips */}
      {categoryCounts.length > 0 && (
        <div className="bg-white rounded-lg shadow px-3 py-2 flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setCategory('all')}
            className={`px-2 py-1 text-xs font-medium rounded ${
              category === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({categoryCounts.reduce((acc, c) => acc + c.high_signal, 0)} signal / {categoryCounts.reduce((acc, c) => acc + c.total, 0)} total)
          </button>
          {categoryCounts.filter(c => c.total > 0).map(c => {
            const meta = CATEGORY_META[c.category] || CATEGORY_META.unknown;
            return (
              <button
                key={c.category}
                onClick={() => setCategory(c.category)}
                className={`px-2 py-1 text-xs font-medium rounded ${
                  category === c.category ? 'ring-2 ring-blue-500 ' + meta.cls : meta.cls + ' hover:opacity-80'
                }`}
              >
                {meta.label} <span className="opacity-70">{c.high_signal}/{c.total}</span>
              </button>
            );
          })}
        </div>
      )}

      {error && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900 flex items-start justify-between gap-2">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-blue-600 underline shrink-0">dismiss</button>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-gray-500 bg-white rounded-lg shadow">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center bg-white rounded-lg shadow">
          <p className="text-2xl mb-2">✨</p>
          <p className="text-gray-700 font-medium">All caught up.</p>
          <p className="text-xs text-gray-500 mt-1">No threads matching the filter — or email sync hasn't run yet (try the "📧 Sync emails" button above).</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map(r => {
            const days = daysAgo(r.last_sent_at);
            const tone = toneFor(days);
            const meta = r.last_ai_category ? (CATEGORY_META[r.last_ai_category] || CATEGORY_META.unknown) : null;
            const score = r.last_ai_importance_score;
            const scoreClass = score == null ? 'text-gray-400'
              : score >= 80 ? 'text-red-600 font-bold'
              : score >= 60 ? 'text-amber-600 font-semibold'
              : 'text-gray-500';
            return (
              <li
                key={r.thread_id}
                className={`border-2 rounded-lg p-3 ${tone}`}
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 text-center w-12">
                    <div className="text-xl font-bold">{days}</div>
                    <div className="text-[10px] uppercase tracking-wider">days</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">
                        {r.contact_name || r.last_from_name || r.last_from_email}
                      </span>
                      {r.company_id && r.company_name && (
                        <Link to={`/admin/crm/companies/${r.company_id}`} className="text-xs text-blue-700 hover:underline">
                          {r.company_name}
                        </Link>
                      )}
                      {meta && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.cls}`}>
                          {meta.label}
                        </span>
                      )}
                      {score != null && (
                        <span className={`text-[10px] ${scoreClass}`} title={`AI importance score ${score}/100`}>
                          {score}/100
                        </span>
                      )}
                      <span className="text-xs text-gray-500">{r.last_from_email}</span>
                    </div>
                    {r.last_ai_summary && (
                      <div className="text-xs text-gray-700 italic mt-1">🧠 {r.last_ai_summary}</div>
                    )}
                    <div className="text-sm text-gray-900 mt-1 truncate">{r.last_subject || '(no subject)'}</div>
                    {r.last_preview && (
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">{r.last_preview}</p>
                    )}
                    <div className="text-[10px] text-gray-500 mt-1">
                      Received {formatRelative(r.last_sent_at)}
                    </div>
                  </div>
                  <div className="shrink-0 flex flex-col gap-1.5">
                    {r.linked_contact_id && (
                      <Link
                        to={`/admin/crm/contacts/${r.linked_contact_id}`}
                        className="text-xs px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Open contact
                      </Link>
                    )}
                    {r.last_from_email && (
                      <button
                        onClick={() => setReplyingTo(r)}
                        className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Reply now
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (!window.confirm('Hide this thread from Awaiting Reply? Marks every email in it as noise — the AI will treat similar future emails with more skepticism over time.')) return;
                        try {
                          await fetch(`/api/crm/threads/${r.thread_id}/hide`, {
                            method: 'PATCH', headers: authHeaders()
                          });
                          // Optimistic remove
                          setRows(prev => prev.filter(x => x.thread_id !== r.thread_id));
                        } catch (err: any) {
                          setError(err.message);
                        }
                      }}
                      className="text-xs px-2 py-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Mark as noise — hides this thread + future emails in it"
                    >
                      ✕ Hide
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="text-xs text-gray-500">
        Emails sync from your Outlook inbox every 15 min once Mail.Read is granted in Azure. Threads where you've replied
        more recently are automatically removed from this list.
      </div>

      {replyingTo && (
        <ComposeEmailModal
          replyToEmailId={replyingTo.last_email_id}
          to={replyingTo.last_from_email || undefined}
          toName={replyingTo.contact_name || replyingTo.last_from_name || undefined}
          replyOriginalSubject={replyingTo.last_subject || undefined}
          replyOriginalPreview={replyingTo.last_preview || undefined}
          onClose={() => setReplyingTo(null)}
          onSent={() => {
            setReplyingTo(null);
            // Optimistic: remove this thread from the list immediately;
            // background sync (kicked off server-side) confirms within seconds.
            setRows(prev => prev.filter(r => r.thread_id !== replyingTo.thread_id));
            setError(`Reply sent to ${replyingTo.contact_name || replyingTo.last_from_email}.`);
            setTimeout(() => setError(null), 5000);
          }}
        />
      )}
    </div>
  );
};
